import React, {useEffect, useMemo, useState} from 'react';
import Translate from '../../components/Translate';
import {formatSecondsToHHMMSS} from '../../utils/General';
import {getOperatorTableData} from '../../services/statistics/api';
import Drawer from '../ui/Drawer';
import LoadingComponent from '../ui/LoadingComponent';
import SilverButton from '../buttons/SilverButton';
import ContactAvatar from './ContactAvatar';
import ColorIndicator from './ColorIndicator';
import RatingStars from './RatingStars';
import LevelBadge from './LevelBadge';
import AwardMedal from './AwardMedal';
import Tooltip from './Tooltip';
import ProfileImageViewer from '../../components/ProfileImageViewer';
import XIcon from '../../assets/icons/XIcon';
import './OperatorProfileDrawer.css';
import config from "../../config";

const emptyStats = {};

const getOperatorName = (operator) => {
    if (!operator) return '';
    if (operator.fullName) return operator.fullName;
    return [operator.firstName, operator.lastName].filter(Boolean).join(' ');
};

const isUnlimited = (value) => Number(value) >= 9999;

const formatLimit = (value) => {
    if (value == null) return 'N/A';
    return isUnlimited(value) ? <Translate text="unlimited"/> : value;
};

const formatTime = (value) => formatSecondsToHHMMSS(value);

const InfoRow = ({label, value}) => (
    <div className="operator-profile-drawer__row">
        <span className="operator-profile-drawer__label">
            <Translate text={label}/>
        </span>
        <span className="operator-profile-drawer__value">
            {value == null || value === '' ? 'N/A' : value}
        </span>
    </div>
);

const StatCard = ({label, value, tone = 'default'}) => (
    <div className={`operator-profile-drawer__stat-card operator-profile-drawer__stat-card--${tone}`}>
        <span className="operator-profile-drawer__stat-value">{value == null ? 0 : value}</span>
        <span className="operator-profile-drawer__stat-label"><Translate text={label}/></span>
    </div>
);

const Section = ({title, children}) => (
    <section className="operator-profile-drawer__section">
        <h4 className="operator-profile-drawer__section-title">
            <Translate text={title}/>
        </h4>
        {children}
    </section>
);

const OperatorProfileDrawer = ({
                                   open,
                                   onClose,
                                   operator,
                                   parentCompanyId,
                                   filters = {},
                                   width = 480
                               }) => {
    const [stats, setStats] = useState(emptyStats);
    const [lifetimeStats, setLifetimeStats] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isPhotoOpen, setIsPhotoOpen] = useState(false);

    const operatorId = operator && operator.id;

    const companyIds = filters.companyIds || filters.companies || [];

    /*
     * Keyed by what the ids are, not by which array they arrived in.
     *
     * Every caller writes the filters inline - filters={{companyIds: subIds}} -
     * so a fresh object with a fresh array turns up on each of the parent's
     * renders. Depending on the array itself meant this memo was rebuilt every
     * time, the fetch below saw a new value and ran again, and the whole panel
     * dropped into its loading state.
     *
     * On the dashboard the page behind refreshes its figures every thirty
     * seconds, so an open profile blinked on a timer while showing figures that
     * had not changed.
     */
    const companyIdsKey = companyIds.join(',');

    const requestFilters = useMemo(() => ({
        companyIds,
        start: filters.start || filters.startDate || null,
        end: filters.end || filters.endDate || null,
    }), [
        companyIdsKey,
        filters.start,
        filters.startDate,
        filters.end,
        filters.endDate,
    ]);

    useEffect(() => {
        if (!open || !operatorId || !parentCompanyId) {
            return;
        }

        setIsLoading(true);
        setHasError(false);

        getOperatorTableData(parentCompanyId, operatorId, requestFilters)
            .then((response) => {
                setStats(response || emptyStats);
            })
            .catch(() => {
                setStats(emptyStats);
                setHasError(true);
            })
            .finally(() => setIsLoading(false));
    }, [open, operatorId, parentCompanyId, requestFilters]);

    /*
     * The level is a career standing, not a figure for the week on screen.
     *
     * Everything else in this panel answers to the filters the page was opened
     * with, and that is right - you came here asking about a period. A level
     * read off the same numbers would say an operator of four years is level 1
     * because the filter is set to yesterday, which is not a smaller truth but
     * a wrong one. So when a period is in play the level is fetched again with
     * the dates dropped; with no period the panel's own figures are already the
     * whole of it and no second call is made.
     */
    const hasPeriod = !!(requestFilters.start || requestFilters.end);

    useEffect(() => {
        if (!open || !operatorId || !parentCompanyId || !hasPeriod) {
            setLifetimeStats(null);
            return;
        }

        getOperatorTableData(parentCompanyId, operatorId, {companyIds: requestFilters.companyIds})
            .then((response) => setLifetimeStats(response || null))
            .catch(() => setLifetimeStats(null));
    }, [open, operatorId, parentCompanyId, hasPeriod, requestFilters.companyIds]);

    useEffect(() => {
        if (!open) setIsPhotoOpen(false);
    }, [open]);

    const viewOperator = stats && stats.id ? stats : (operator || emptyStats);
    const fullName = getOperatorName(viewOperator) || getOperatorName(operator) || 'N/A';
    const ratingInStars = ((viewOperator.rating || 0) * 5) / 100;
    const roleName = viewOperator.role
        ? ((config.roles || []).find(r => r.id === viewOperator.role) || {}).name || viewOperator.role
        : 'N/A';

    /*
     * From the lifetime answer where there is one. A level is a career and not a
     * fortnight, so when the drawer is opened under a date filter it asks a
     * second time without the dates - and that second answer is the one the
     * badge comes from.
     */
    const rankSource = lifetimeStats || viewOperator;
    const level = Number(rankSource.level) || 1;

    // no photo, nothing to enlarge - the coloured initials are already full size
    const photoUrl = viewOperator.thumbUrl || (operator && operator.thumbUrl) || '';

    /*
     * Escape and the backdrop belong to whatever is on top. The drawer listens
     * for Escape itself, so with the photo open one press would close both - you
     * would lose the panel for asking to put the picture away. The X in the
     * header still closes the drawer outright, which is what it is there for.
     */
    const handleClose = () => {
        if (isPhotoOpen) {
            setIsPhotoOpen(false);
            return;
        }
        onClose();
    };

    return (
        <Drawer
            open={open}
            onClose={handleClose}
            side="right"
            width={width}
            className="conversation-history-drawer operator-profile-drawer"
            overlayClassName="conversation-history-drawer-overlay operator-profile-drawer-overlay"
            paperStyle={{maxWidth: 'calc(100vw - 16px)'}}
            background="rgba(0, 0, 0, 0.35)"
        >
            <div className="conversation-history-drawer__shell">
                <div className="conversation-history-drawer__header">
                    <span>
                        <Translate text="userProfile"/>
                    </span>
                    <SilverButton type="button" variant="ghost" iconOnly expandedHover width="20px" height="20px"
                                  onClick={onClose}>
                        <XIcon width={20} height={20} stroke="currentColor"/>
                    </SilverButton>
                </div>

                <div className="conversation-history-drawer__content operator-profile-drawer__content">
                    {isLoading ? (
                        <LoadingComponent containerStyles={{margin: '120px 0'}}/>
                    ) : hasError ? (
                        <div className="operator-profile-drawer__empty">
                            <Translate text="somethingWentWrong"/>
                        </div>
                    ) : (
                        <React.Fragment>
                            <div className="operator-profile-drawer__profile-card">
                                {photoUrl ? (
                                    <button
                                        type="button"
                                        className="operator-profile-drawer__avatar-button"
                                        onClick={() => setIsPhotoOpen(true)}
                                    >
                                        <ContactAvatar contact={viewOperator} size={58}/>
                                    </button>
                                ) : (
                                    <ContactAvatar contact={viewOperator} size={58}/>
                                )}
                                <div className="operator-profile-drawer__profile-main">
                                    <div className="operator-profile-drawer__name">{fullName}</div>
                                    <div className="operator-profile-drawer__email">{viewOperator.email || 'N/A'}</div>
                                    <div className="operator-profile-drawer__badges">
                                        <div className="operator-profile-drawer__status">
                                            <ColorIndicator currentStatus={viewOperator.currentStatus}/>
                                            <Translate text={viewOperator.currentStatus || 'N/A'}/>
                                            {viewOperator.currentStatusTime ? ` (${viewOperator.currentStatusTime})` : ''}
                                        </div>
                                        {/* the word alone: the rule behind the
                                            ladder belongs on the page that
                                            explains it, not on every badge */}
                                        <Tooltip text="Level" style={{display: 'flex'}}>
                                            <LevelBadge level={level}/>
                                        </Tooltip>

                                        {/* what they have won, beside what they
                                            are - the same medal they wear in
                                            their own header, so a manager and
                                            the operator are looking at one thing */}
                                        <AwardMedal awards={viewOperator.awards}/>
                                    </div>
                                </div>
                            </div>

                            <Section title="requests">
                                <div className="operator-profile-drawer__stats-grid">
                                    <StatCard label="allRequests" value={viewOperator.allChats}/>
                                    <StatCard label="todaysRequests" value={viewOperator.todaysChats}/>
                                    <StatCard label="answeredRequestsToOperator" value={viewOperator.answeredChats} tone="success"/>
                                    <StatCard label="unansweredRequestsToOperator" value={viewOperator.unansweredChats} tone="danger"/>
                                    <StatCard label="activeRequests" value={viewOperator.activeChats}/>
                                    <StatCard label="forwardedRequestsToOperator" value={viewOperator.forwardedChats}/>
                                </div>
                            </Section>

                            <Section title="time">
                                <InfoRow label="avrgAnswerSpeed" value={formatTime(viewOperator.avrgAnswerSpeed)}/>
                                <InfoRow label="avrgFirstAnswerSpeed" value={formatTime(viewOperator.avrgFirstAnswerSpeed)}/>
                                <InfoRow label="avrgConvTime" value={formatTime(viewOperator.avrgConvTime)}/>
                            </Section>

                            <Section title="main">
                                <InfoRow
                                    label="Rating"
                                    value={
                                        <span className="operator-profile-drawer__rating">
                                            <RatingStars value={ratingInStars}/>
                                            <span>{viewOperator.rating || 0}% / {viewOperator.ratingCount || 0}</span>
                                        </span>
                                    }
                                />
                                <InfoRow label="currentChatLimit" value={formatLimit(viewOperator.currentChatLimit)}/>
                                <InfoRow label="chatLimit" value={formatLimit(viewOperator.chatLimit)}/>
                                <InfoRow label="Role" value={roleName}/>
                            </Section>
                        </React.Fragment>
                    )}
                </div>
            </div>

            {isPhotoOpen && (
                <ProfileImageViewer
                    imageUrl={photoUrl}
                    close={() => setIsPhotoOpen(false)}
                    zIndex={60002}
                />
            )}
        </Drawer>
    );
};

export default OperatorProfileDrawer;



// WEBPACK FOOTER //
// ./src/components-new/general/OperatorProfileDrawer.jsx